import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Wrench,
  Users,
  Sparkles,
  BookOpen,
  Check,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CTA, Eyebrow } from "@/components/ui-kit";
import { Seo } from "@/components/Seo";
import { captureLead, formatError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { IMAGES } from "@/lib/content";

/* -------- Content constants (kept inline so the homepage is self-documenting) -------- */

const KINGDOM_PRINCIPLES = [
  { a: "Faith", b: "Business" },
  { a: "Character", b: "Cash" },
  { a: "Service", b: "Sales" },
  { a: "Stewardship", b: "Success" },
  { a: "Legacy", b: "Lifestyle" },
];

const JOURNEY = [
  {
    n: "01",
    tag: "The Shift",
    icon: Compass,
    title: "Think Different.",
    body:
      "Renew the mind. Discover purpose. Recognize the value God placed inside you. Begin thinking like a builder.",
    to: "/book",
    cta: "Explore The Shift",
    testid: "journey-shift",
  },
  {
    n: "02",
    tag: "The System",
    icon: Wrench,
    title: "Build Different.",
    body:
      "Turn ideas, skills, knowledge, and value into systems capable of producing real business results.",
    to: "/system",
    cta: "Discover The System",
    testid: "journey-system",
  },
  {
    n: "03",
    tag: "Builders Circle",
    icon: Users,
    title: "Grow Together.",
    body:
      "Community, accountability, leadership, implementation, and continued development for people building lives that last.",
    to: "/builders-circle",
    cta: "Explore Builders Circle",
    testid: "journey-circle",
  },
];

const OWNERSHIP_CONTRASTS = [
  { left: "Consumer", right: "Creator" },
  { left: "Dependency", right: "Ownership" },
  { left: "Task execution", right: "System creation" },
  { left: "Waiting for opportunity", right: "Creating value" },
];

const FAQS = [
  {
    q: "Do I need to quit my job?",
    a:
      "No. Employee 2 Employer is not about recklessly quitting employment. It is about changing how you think, learning how ownership works, creating value, and building wisely.",
  },
  {
    q: "Is Employee 2 Employer only for employees?",
    a:
      "No. It is also for struggling entrepreneurs and builders who need greater clarity, structure, systems, and purpose.",
  },
  {
    q: "Is this a Christian company?",
    a:
      "Employee 2 Employer is openly built on faith in God and biblical principles. Our goal is to serve people with excellence regardless of where they currently are in their faith journey.",
  },
  {
    q: "Where should I start?",
    a: "Start with The Shift.",
  },
  {
    q: "What comes after The Shift?",
    a:
      "The System provides implementation, and Builders Circle provides community and continued development.",
  },
];

/* --------------------------------- Component ---------------------------------- */

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Break the Employee Mindset · Employee 2 Employer"
        description="Employee 2 Employer helps you renew your mind, recognize your value, build with purpose, and begin creating something you can own. Start with The Shift by Don Orr Jr."
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: "Employee 2 Employer: The Shift",
          author: { "@type": "Person", name: "Don Orr Jr." },
          publisher: "Employee 2 Employer LLC",
          description:
            "Break the employee mindset. Build the life God created you for.",
        }}
      />

      {/* 1. HERO */}
      <HeroSection />

      {/* 2. PROBLEM */}
      <ProblemSection />

      {/* 3. THE SHIFT */}
      <ShiftSection />

      {/* 4. KINGDOM FOUNDATION */}
      <KingdomSection />

      {/* 5. E2E JOURNEY */}
      <JourneySection />

      {/* 6. OWNERSHIP */}
      <OwnershipSection />

      {/* 7. SERVICE & VALUE */}
      <ServiceSection />

      {/* 8. FOUNDER */}
      <FounderSection />

      {/* 9. MOVEMENT */}
      <MovementSection />

      {/* 10. THE SHIFT OFFER */}
      <OfferSection />

      {/* 11. EMAIL / MOVEMENT CTA */}
      <JoinSection />

      {/* 12. FAQ */}
      <FaqSection />
    </div>
  );
}

/* --------------------------- Section components -------------------------- */

function HeroSection() {
  return (
    <section
      id="hero"
      className="relative bg-black noise pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden"
    >
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Employee 2 Employer · A Book by Don Orr Jr.</Eyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-white mt-7 tracking-tight leading-[0.98] text-[11vw] sm:text-6xl lg:text-[5.4rem] lg:leading-[1.02]">
              Break the employee mindset.
              <br />
              <span className="text-[#D4AF37] italic">Build the life God created you for.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="font-body text-lg lg:text-xl text-white/60 font-light mt-9 max-w-xl leading-relaxed">
              You were created for more than survival. Employee 2 Employer helps you renew your
              mind, recognize your value, build with purpose, and begin creating something you
              can own.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="flex flex-wrap gap-4 mt-11">
              <CTA
                to="/checkout"
                testId="hero-get-shift"
                track="get_the_shift"
                location="home_hero"
              >
                Get The Shift
              </CTA>
              <a
                href="#journey"
                data-testid="hero-discover-journey"
                data-track="cta_click"
                data-track-label="discover_journey"
                data-track-location="home_hero"
                className="inline-flex items-center gap-3 font-body font-medium text-[15px] px-9 py-4 text-white border border-white/35 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
              >
                Discover the E2E Journey
              </a>
            </div>
          </Reveal>
        </div>

        {/* Book */}
        <div className="lg:col-span-5 relative flex items-center justify-center min-h-[460px] lg:-mt-8">
          <img
            src={IMAGES.book}
            alt="The Shift — book cover"
            data-testid="hero-book-image"
            loading="eager"
            className="relative z-10 max-h-[560px] lg:max-h-[620px] w-auto animate-float-book"
            style={{
              filter:
                "drop-shadow(0 55px 45px rgba(0,0,0,0.55)) drop-shadow(0 20px 25px rgba(0,0,0,0.35))",
            }}
          />
        </div>
      </div>
    </section>
  );
}

function ProblemSection() {
  return (
    <section className="bg-white text-black py-28 lg:py-40">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Reveal>
            <Eyebrow className="!text-[#B8952E]">The Real Cost</Eyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.06] tracking-tight mt-7">
              You're not afraid of work.
              <br />
              You're tired of working without{" "}
              <span className="text-[#B8952E] italic">ownership.</span>
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:pt-6">
          <Reveal delay={140}>
            <div className="space-y-5 font-body text-lg text-black/70 leading-relaxed">
              <p>
                You show up. You handle responsibilities. You pay bills, care for the people
                depending on you, and repeat the cycle again next week.
              </p>
              <p>
                And somewhere, quietly, you know there's more inside you. More capacity. More
                calling. More that God placed there than the world has asked you to use.
              </p>
              <p className="text-black">
                The enemy is not employment. The enemy is the belief that you have no other
                options, no value to create, no purpose to pursue, no ability to build.
              </p>
              <p>
                The same discipline you've used to help build someone else's system can begin,
                today, to build something of your own.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ShiftSection() {
  return (
    <section className="relative bg-black noise py-28 lg:py-40 overflow-hidden">
      <div className="relative z-10 max-w-[1240px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1 flex justify-center">
          <Reveal>
            <img
              src={IMAGES.book}
              alt="The Shift"
              loading="lazy"
              className="max-h-[520px] w-auto animate-float-book"
              style={{
                filter:
                  "drop-shadow(0 55px 45px rgba(0,0,0,0.55)) drop-shadow(0 20px 25px rgba(0,0,0,0.35))",
              }}
            />
          </Reveal>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal><Eyebrow>Where It Begins</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight mt-7">
              Before you build a business,
              <br />
              <span className="text-[#D4AF37] italic">you have to build the person.</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-8 space-y-5 font-body text-lg text-white/65 leading-relaxed max-w-xl">
              <p>
                Strategies change. Platforms change. Technology changes. But identity,
                principles, character, service, stewardship, and the ability to create value
                remain foundational.
              </p>
              <p>
                <span className="text-white/90">The Shift</span> is where the journey begins.
                Not another course. Not another hustle manual. A foundation.
              </p>
            </div>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-11">
              <CTA
                to="/checkout"
                testId="shift-section-cta"
                track="start_your_shift"
                location="home_shift"
              >
                Start Your Shift
              </CTA>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function KingdomSection() {
  return (
    <section className="bg-white text-black py-28 lg:py-40" data-testid="kingdom-foundation">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <Reveal><Eyebrow className="!text-[#B8952E]">Kingdom Foundation</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] tracking-tight mt-6">
              Some things come first.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="font-body text-black/60 text-lg mt-6 leading-relaxed">
              Financial success matters. But it is never the highest objective. These are the
              orders of priority Employee 2 Employer is built on.
            </p>
          </Reveal>
        </div>

        <ol className="mt-16 divide-y divide-black/10 border-t border-b border-black/10">
          {KINGDOM_PRINCIPLES.map((p, i) => (
            <Reveal key={p.a} delay={i * 60}>
              <li className="grid grid-cols-12 gap-6 py-7 lg:py-9 items-center">
                <span className="col-span-2 lg:col-span-1 font-body text-xs tracking-[0.24em] uppercase text-black/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="col-span-10 lg:col-span-11 font-display text-2xl sm:text-3xl lg:text-4xl leading-tight">
                  <span className="text-black">{p.a}</span>{" "}
                  <span className="text-black/40 italic font-normal">before</span>{" "}
                  <span className="text-[#B8952E]">{p.b}.</span>
                </p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function JourneySection() {
  return (
    <section id="journey" className="bg-[#0a0a0a] noise py-28 lg:py-40" data-testid="journey-section">
      <div className="relative z-10 max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>The E2E Journey</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] tracking-tight mt-6">
              Three steps. One ecosystem.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="font-body text-white/55 text-lg mt-6 leading-relaxed">
              The Shift, The System, and Builders Circle are not separate products. They are
              the sequence of a single journey — from mindset, to implementation, to
              community.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 relative">
          {/* subtle gold connector line */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute left-0 right-0 top-16 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {JOURNEY.map((s, i) => (
              <Reveal key={s.n} delay={i * 100}>
                <article
                  data-testid={s.testid}
                  className="relative border border-white/12 p-8 lg:p-10 h-full flex flex-col hover:border-[#D4AF37]/60 transition-colors duration-500 group bg-black/40"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-display text-[#D4AF37] text-3xl leading-none">
                      {s.n}
                    </span>
                    <s.icon size={18} className="text-white/45 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.4} />
                  </div>
                  <p className="font-body text-xs tracking-[0.24em] uppercase text-white/45 mt-7">
                    {s.tag}
                  </p>
                  <h3 className="font-display text-2xl lg:text-3xl text-white mt-3 leading-snug">
                    {s.title}
                  </h3>
                  <p className="font-body text-white/60 mt-5 leading-relaxed flex-1 text-[15px]">
                    {s.body}
                  </p>
                  <Link
                    to={s.to}
                    data-track="cta_click"
                    data-track-label={s.testid}
                    data-track-location="home_journey"
                    className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E6C75A] font-body font-medium mt-8 border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1 self-start transition-colors"
                  >
                    {s.cta}
                    <ArrowRight size={14} />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function OwnershipSection() {
  return (
    <section className="bg-white text-black py-28 lg:py-40">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-4xl">
          <Reveal><Eyebrow className="!text-[#B8952E]">Ownership</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.8rem] leading-[1.04] tracking-tight mt-6">
              Employees work in systems.
              <br />
              <span className="text-[#B8952E] italic">Builders create them.</span>
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="font-body text-lg text-black/65 mt-7 leading-relaxed max-w-2xl">
              This is not employee versus entrepreneur. It's consumer versus creator. And it
              starts as a way of thinking — long before it becomes a way of earning.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
          {OWNERSHIP_CONTRASTS.map((c, i) => (
            <Reveal key={c.left} delay={i * 70}>
              <div className="grid grid-cols-12 items-baseline border-t border-black/10 py-6">
                <span className="col-span-5 font-body text-black/45 line-through decoration-black/25 text-lg">
                  {c.left}
                </span>
                <ArrowRight size={16} className="col-span-2 text-[#B8952E]" />
                <span className="col-span-5 font-display text-2xl text-black">{c.right}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceSection() {
  return (
    <section className="bg-black noise py-28 lg:py-40">
      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <Reveal><Eyebrow>Service & Value</Eyebrow></Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.06] tracking-tight mt-7">
            Money follows <span className="text-[#D4AF37] italic">value.</span>
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <div className="mt-10 space-y-5 font-body text-lg text-white/60 leading-relaxed">
            <p>The goal isn't simply to make money. The right questions come first:</p>
          </div>
        </Reveal>
        <Reveal delay={220}>
          <ul className="mt-8 space-y-4 text-left max-w-lg mx-auto">
            {[
              "Who can you serve?",
              "What problem can you solve?",
              "What value can you create?",
              "What system can you build around that value?",
            ].map((q, i) => (
              <li key={q} className="flex items-baseline gap-4">
                <span className="font-display text-[#D4AF37] text-lg leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-white text-xl lg:text-2xl leading-snug">{q}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={300}>
          <p className="font-body italic text-white/50 mt-11 leading-relaxed">
            Entrepreneurship, in its truest form, is a way of serving.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FounderSection() {
  return (
    <section className="bg-white text-black py-28 lg:py-40" data-testid="founder-section">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative">
              <img
                src={IMAGES.portrait}
                alt="Don Orr Jr., Founder of Employee 2 Employer"
                loading="lazy"
                className="w-full max-w-md mx-auto grayscale-[15%]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none border border-black/8"
              />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal><Eyebrow className="!text-[#B8952E]">Founder</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.06] tracking-tight mt-6">
              Don Orr Jr.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="font-body text-black/55 uppercase tracking-[0.2em] text-xs mt-3">
              Founder, Employee 2 Employer
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-8 space-y-5 font-body text-lg text-black/70 leading-relaxed">
              <p>
                Employee 2 Employer wasn't built from theory. It was built from lived
                experience — of wanting more, working hard, struggling financially, and
                rethinking almost everything about money, mindset, credit, business, and
                purpose.
              </p>
              <p>
                At a certain point the picture became clear: becoming valuable enough to help
                other people solve their problems changes everything.
              </p>
              <blockquote className="border-l-2 border-[#B8952E] pl-6 py-1 font-display italic text-2xl lg:text-3xl text-black leading-snug">
                "You can have everything in life you want if you help enough other people get
                what they want."
                <footer className="mt-3 text-sm not-italic font-body tracking-[0.16em] uppercase text-black/50">
                  — Zig Ziglar
                </footer>
              </blockquote>
              <p>
                Employee 2 Employer is the company that came from taking that idea seriously —
                and inviting others onto the same journey.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function MovementSection() {
  return (
    <section className="relative bg-black noise py-32 lg:py-44 overflow-hidden">
      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <Reveal><Eyebrow>The Movement</Eyebrow></Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-[3.8rem] leading-[1.04] tracking-tight mt-8">
            This is bigger <span className="text-[#D4AF37] italic">than business.</span>
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <p className="font-body text-white/60 text-lg mt-8 leading-relaxed">
            Business can create income. Ownership can create freedom. But neither is the final
            destination. Employee 2 Employer exists to develop builders who —
          </p>
        </Reveal>
        <Reveal delay={240}>
          <ul className="mt-11 grid grid-cols-2 sm:grid-cols-4 gap-y-6 gap-x-4 max-w-2xl mx-auto">
            {[
              "Honor God.",
              "Serve people.",
              "Lead families.",
              "Create value.",
              "Practice stewardship.",
              "Build systems.",
              "Create opportunity.",
              "Leave a legacy.",
            ].map((v) => (
              <li key={v} className="font-body text-white/80 text-[15px] tracking-wide">
                {v}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

function OfferSection() {
  return (
    <section id="offer" className="bg-[#0a0a0a] noise py-28 lg:py-40" data-testid="offer-section">
      <div className="relative z-10 max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5 flex justify-center">
            <Reveal>
              <img
                src={IMAGES.book}
                alt="Employee 2 Employer: The Shift"
                loading="lazy"
                className="max-h-[540px] w-auto animate-float-book"
                style={{
                  filter:
                    "drop-shadow(0 55px 45px rgba(0,0,0,0.55)) drop-shadow(0 20px 25px rgba(0,0,0,0.35))",
                }}
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>The Book</Eyebrow></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.06] tracking-tight mt-6">
                Employee 2 Employer: <span className="text-[#D4AF37] italic">The Shift</span>
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="font-body text-white/60 text-lg mt-5 max-w-xl leading-relaxed">
                Break the employee mindset. Build the life God created you for.
              </p>
              <p className="font-body text-white/45 text-sm mt-3 tracking-[0.16em] uppercase">
                By Don Orr Jr.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-10 flex items-baseline gap-5 border-t border-b border-white/10 py-6">
                <span className="font-display text-5xl text-[#D4AF37]">$19</span>
                <span className="font-body text-sm tracking-[0.18em] uppercase text-white/50">
                  Digital Book · Instant Access
                </span>
              </div>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-10 flex flex-wrap gap-4 items-center">
                <CTA
                  to="/checkout"
                  testId="offer-get-shift"
                  track="get_the_shift"
                  location="home_offer"
                  className="!px-10 !py-5"
                >
                  Get The Shift — $19
                </CTA>
                <Link
                  to="/preview"
                  data-testid="offer-preview-link"
                  className="inline-flex items-center gap-2 text-white/70 hover:text-[#D4AF37] font-body font-medium border-b border-white/25 hover:border-[#D4AF37] pb-1 transition-colors"
                >
                  <BookOpen size={14} /> Or start with Chapter One
                </Link>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <p className="font-body text-white/40 text-sm mt-6">
                Instant digital access. Start your shift today.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function JoinSection() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setError("");
    setState("loading");
    try {
      await captureLead({
        name: form.name.trim(),
        email: form.email.trim(),
        source_page: "home-join-movement",
        tags: ["Newsletter", "Movement"],
      });
      track.newsletter("home-join-movement", ["Newsletter", "Movement"]);
      setState("done");
    } catch (err) {
      setState("idle");
      setError(formatError(err.response?.data?.detail) || "Something went wrong. Try again.");
    }
  };

  return (
    <section className="bg-white text-black py-28 lg:py-36" data-testid="join-movement">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal><Eyebrow className="!text-[#B8952E]">Join The Movement</Eyebrow></Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-5xl leading-[1.06] tracking-tight mt-6">
            <Sparkles size={22} className="inline text-[#B8952E] mr-2 -mt-2" />
            Build something that lasts.
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="font-body text-black/60 text-lg mt-6 max-w-xl mx-auto leading-relaxed">
            Receive practical lessons on faith, ownership, entrepreneurship, stewardship,
            systems, and building a life that lasts.
          </p>
        </Reveal>

        {state === "done" ? (
          <Reveal delay={200}>
            <div
              data-testid="join-success"
              className="mt-10 inline-flex items-center gap-3 border border-[#B8952E]/40 bg-[#D4AF37]/8 px-6 py-4"
            >
              <Check size={18} className="text-[#B8952E]" />
              <span className="font-body">Welcome. Check your inbox.</span>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={200}>
            <form
              onSubmit={submit}
              data-testid="join-form"
              className="mt-10 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 max-w-xl mx-auto text-left"
              noValidate
            >
              <input
                type="text"
                autoComplete="given-name"
                placeholder="First name"
                value={form.name}
                onChange={onChange("name")}
                data-testid="join-name-input"
                className="bg-white border border-black/20 px-5 py-4 font-body outline-none focus:border-[#B8952E] transition-colors"
              />
              <input
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={onChange("email")}
                data-testid="join-email-input"
                className="bg-white border border-black/20 px-5 py-4 font-body outline-none focus:border-[#B8952E] transition-colors"
              />
              <button
                type="submit"
                disabled={state === "loading"}
                data-testid="join-submit"
                className="bg-black hover:bg-[#B8952E] text-white font-body font-medium px-7 py-4 transition-colors duration-300 disabled:opacity-60"
              >
                {state === "loading" ? "Joining…" : "Join The E2E Community"}
              </button>
              {error && (
                <p className="sm:col-span-3 text-sm text-red-600 font-body" data-testid="join-error">
                  {error}
                </p>
              )}
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-black noise py-28 lg:py-36" data-testid="faq-section">
      <div className="relative z-10 max-w-[900px] mx-auto px-6">
        <div className="text-center max-w-xl mx-auto">
          <Reveal><Eyebrow>Questions</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-white text-4xl sm:text-5xl leading-[1.06] tracking-tight mt-6">
              Good ones deserve real answers.
            </h2>
          </Reveal>
        </div>

        <ul className="mt-16 divide-y divide-white/10 border-t border-b border-white/10">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <li key={f.q}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-6 py-7 text-left group"
                >
                  <span className="font-display text-xl lg:text-2xl text-white group-hover:text-[#D4AF37] transition-colors">
                    {f.q}
                  </span>
                  <span
                    className={`font-display text-2xl text-[#D4AF37] transition-transform duration-500 ${
                      isOpen ? "rotate-45" : ""
                    }`}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100 pb-8" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="font-body text-white/65 leading-relaxed max-w-2xl pr-10">
                      {f.a}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-16 text-center">
          <CTA to="/checkout" testId="faq-get-shift" track="get_the_shift" location="home_faq">
            Get The Shift
          </CTA>
        </div>
      </div>
    </section>
  );
}
